import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiService } from '../services/api';

const pct = (v?: number | null, d = 1) => (v == null ? '–' : `${(v * 100).toFixed(d)}%`);
const signed = (v?: number | null) => (v == null ? '–' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);

function ResearchNote({ r }: { r: any }) {
  if (!r?.all) return null;
  const a = r.all;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <strong>Tested on {a.reports} reports of {a.stocks} S&amp;P 500 stocks since 2019:</strong> the move was{' '}
      {(a.median_ratio * 100).toFixed(0)}% of what the options priced in at the median, and bigger than it{' '}
      {a.beat_straddle_pct}% of the time. Selling the straddle and holding to expiry returned{' '}
      {a.sell_straddle_net_avg_pct}% of the price per report after the spread (won {a.sell_net_wins_pct}% of the time,
      worst {a.sell_net_worst_pct}%).
      {r.persistence && (r.persistence.p > 0.1
        ? ` A stock's own record did not carry over: its ratio in 2019–22 said nothing about 2023–26 (rank correlation ${r.persistence.rank_correlation}), so read the past reports below as a description, not a forecast.`
        : ` A stock's own ratio carried over from 2019–22 to 2023–26 (rank correlation ${r.persistence.rank_correlation}, p ${r.persistence.p}).`)}
    </Alert>
  );
}

export default function Earnings() {
  const [sym, setSym] = useState('');
  const [data, setData] = useState<any | null>(null);
  const [upcoming, setUpcoming] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { apiService.getUpcomingEarnings().then(setUpcoming).catch(() => undefined); }, []);
  const look = (s?: string) => {
    const q = (s ?? sym).trim().toUpperCase();
    if (!q) return;
    setSym(q); setLoading(true); setError(null);
    apiService.getEarnings(q).then(setData).catch(e => setError(e?.message || 'Failed')).finally(() => setLoading(false));
  };
  const p = data?.priced_in;
  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Earnings Moves</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Before a company reports: how big a move the options price in for the report day, next to how much the stock
        really moved on its past reports. The priced-in move is what is left of the options' implied volatility after
        taking out the stock's normal daily swings.
      </Typography>
      <ResearchNote r={data?.research ?? upcoming?.research} />
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" label="Symbol" value={sym} onChange={e => setSym(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && look()} />
        <Button variant="contained" onClick={() => look()}>Look up</Button>
      </Box>
      {upcoming?.reports?.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Reporting in the next 3 weeks — your holdings and Top Picks</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {upcoming.reports.map((r: any) => (
              <Chip key={r.symbol} clickable onClick={() => look(r.symbol)} color={r.held ? 'primary' : 'default'}
                label={`${r.symbol} · ${r.date} ${r.before_open ? 'before open' : 'after close'}`} />
            ))}
          </Box>
        </Paper>
      )}
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {data && !loading && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">{data.symbol} · ${data.price?.toFixed(2)}</Typography>
                {data.next ? (
                  <Typography variant="h6">Next report {data.next.date} ({data.next.before_open ? 'before the open' : 'after the close'}), in {data.next.days} days</Typography>
                ) : <Typography variant="h6">No report date announced</Typography>}
                {p?.implied_move != null ? (
                  <>
                    <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>±{pct(p.implied_move)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      priced in for the report day (options expiring {p.expiry}; implied volatility {pct(p.atm_iv, 0)} vs
                      its normal {pct(p.normal_vol, 0)}{p.straddle_pct != null ? `; the whole straddle costs ${pct(p.straddle_pct)}` : ''})
                    </Typography>
                  </>
                ) : p?.note ? <Alert severity="warning" sx={{ mt: 1 }}>{p.note}</Alert>
                  : data.next && data.next.days <= 45 ? <Alert severity="warning" sx={{ mt: 1 }}>No option prices available right now.</Alert> : null}
                {data.past_median_move != null && (
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Its last {data.past.length} reports moved it <strong>{pct(data.past_median_move)}</strong> at the median
                    ({pct(data.past_mean_move)} on average).
                    {p?.implied_move != null && ` The options expect ${(p.implied_move / data.past_median_move).toFixed(1)}× its usual move.`}
                  </Typography>
                )}
                {data.own_history && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    In the history test, its moves were {(data.own_history.median_ratio * 100).toFixed(0)}% of the priced-in move
                    at the median over {data.own_history.reports} reports, bigger {pct(data.own_history.beat_pct, 0)} of the time.
                    A stock's own record did not predict its later reports in testing; the overall figure above is the better guide.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Past reports</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>When</TableCell><TableCell align="right">Move</TableCell></TableRow></TableHead>
                <TableBody>
                  {data.past.map((r: any) => (
                    <TableRow key={r.date}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell>{r.before_open ? 'before open' : 'after close'}</TableCell>
                      <TableCell align="right" sx={{ color: r.move >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>{signed(r.move)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
